/**
 * Initiative tracker state and reducer functions (#257 / 07 §13.9.1).
 *
 * Pure, side-effect-free functions operating on plain JSON-serialisable
 * records. `InitiativeState` lives in `Model.sessionState.initiative` as
 * ephemeral client session state — never persisted to the op log.
 *
 * Design note from the spec (07 §13.9.1):
 * > Lives in the Campaign's `session_state.initiative` slot; never persisted
 * > across sessions; cleared on session close.
 *
 * The `hp_scratch` field is included on `InitiativeEntry` even though the
 * Phase-2 post-combat log (#259) will be the primary consumer — the issue
 * brief explicitly asks for it for forward-compat.
 */

/** One participant in the initiative order. */
export interface InitiativeEntry {
  /** Stable client-minted id (never persisted to the op log). */
  id: string;
  /**
   * Optional ref to an existing Campaign entity (Character or creature
   * template). Phase 2 wires entity-picker; for now this is a free-text id
   * or undefined.
   */
  character_ref?: string;
  /** Free-text display name (may override `character_ref`'s canonical name). */
  display_name: string;
  /** Sort key — descending (higher initiative goes first). */
  initiative: number;
  /** Free-text condition tags: "prone", "concentration", "bleeding", etc. */
  conditions: string[];
  /**
   * Optional running HP scratch for #259. `max` is informational only —
   * damage math is the GM's job; the tracker records current HP.
   */
  hp_scratch?: { current: number; max?: number };
  /** Free-text turn notes (GM-private scratchpad for the current encounter). */
  notes?: string;
}

/**
 * Ephemeral initiative tracker state for one combat encounter.
 * Lives at `Model.sessionState.initiative`.
 */
export interface InitiativeState {
  /** Participants in initiative order (sorted descending by `.initiative`). */
  entries: InitiativeEntry[];
  /** Index into `entries` of the current turn (0-based). */
  active_index: number;
  /** Round counter (starts at 1 when the first turn begins). */
  round: number;
}

/** A `InitiativeState` with no participants. */
export const EMPTY_INITIATIVE: InitiativeState = {
  entries: [],
  active_index: 0,
  round: 1,
};

// ---------------------------------------------------------------------------
// Pure reducer helpers — each returns a NEW state, never mutates.
// ---------------------------------------------------------------------------

/** Return a copy of `state.entries` sorted descending by initiative value. */
function sortedEntries(entries: InitiativeEntry[]): InitiativeEntry[] {
  return [...entries].sort((a, b) => b.initiative - a.initiative);
}

/**
 * Add a new participant. The tracker re-sorts by initiative automatically.
 * The `active_index` is preserved (it tracks the *entry id*, not the array
 * slot, so re-sorting doesn't skip turns).
 *
 * CAUTION: after re-sorting the current turn may visually shift position —
 * that is correct and intentional (a participant added mid-turn with a high
 * initiative value goes ahead of the current combatant next round).
 */
export function addEntry(
  state: InitiativeState,
  entry: InitiativeEntry,
): InitiativeState {
  const entries = sortedEntries([...state.entries, entry]);
  // Preserve the active entry identity across re-sort.
  const currentId = state.entries[state.active_index]?.id;
  const active_index = currentId
    ? Math.max(0, entries.findIndex((e) => e.id === currentId))
    : state.active_index;
  return { ...state, entries, active_index };
}

/**
 * Remove a participant by id. If the removed entry was the active entry,
 * `active_index` clamps to the last entry or stays at the same slot (the next
 * combatant takes the turn). `round` is unchanged.
 */
export function removeEntry(
  state: InitiativeState,
  id: string,
): InitiativeState {
  const removed_at = state.entries.findIndex((e) => e.id === id);
  if (removed_at === -1) return state;
  const entries = state.entries.filter((e) => e.id !== id);
  if (entries.length === 0) {
    return { entries: [], active_index: 0, round: state.round };
  }
  // If we removed the currently-active entry, the next combatant (same slot)
  // takes over; if that would be out-of-bounds, wrap to 0.
  let active_index = state.active_index;
  if (removed_at < active_index) {
    active_index = Math.max(0, active_index - 1);
  } else if (removed_at === active_index) {
    active_index = active_index < entries.length ? active_index : 0;
  }
  return { ...state, entries, active_index };
}

/**
 * Advance to the next turn. Wraps to `active_index = 0` and increments
 * `round` when the last entry's turn ends.
 * No-op when `entries` is empty.
 */
export function advanceTurn(state: InitiativeState): InitiativeState {
  if (state.entries.length === 0) return state;
  const next = state.active_index + 1;
  if (next >= state.entries.length) {
    return { ...state, active_index: 0, round: state.round + 1 };
  }
  return { ...state, active_index: next };
}

/**
 * Update a single entry by id. Returns the same state (reference-equal) if
 * the id is not found. Patch is shallow-merged onto the existing entry.
 * Initiative changes re-sort the list; `active_index` follows the entry.
 */
export function updateEntry(
  state: InitiativeState,
  id: string,
  patch: Partial<Omit<InitiativeEntry, 'id'>>,
): InitiativeState {
  const idx = state.entries.findIndex((e) => e.id === id);
  if (idx === -1) return state;
  const patched: InitiativeEntry = { ...state.entries[idx]!, ...patch };
  const entries = sortedEntries(
    state.entries.map((e) => (e.id === id ? patched : e)),
  );
  // Preserve turn identity.
  const currentId = state.entries[state.active_index]?.id;
  const active_index = currentId
    ? Math.max(0, entries.findIndex((e) => e.id === currentId))
    : state.active_index;
  return { ...state, entries, active_index };
}

/**
 * Add or remove a condition string on an entry (toggle-style).
 * Condition matching is case-insensitive trim.
 */
export function toggleCondition(
  state: InitiativeState,
  id: string,
  condition: string,
): InitiativeState {
  const entry = state.entries.find((e) => e.id === id);
  if (!entry) return state;
  const norm = condition.trim().toLowerCase();
  const has = entry.conditions.some((c) => c.trim().toLowerCase() === norm);
  const conditions = has
    ? entry.conditions.filter((c) => c.trim().toLowerCase() !== norm)
    : [...entry.conditions, condition.trim()];
  return updateEntry(state, id, { conditions });
}

/**
 * Apply a scratch-HP delta to an entry. `delta` may be negative (damage) or
 * positive (healing). `max` is preserved from the current entry if not
 * supplied in the existing `hp_scratch`.
 */
export function applyHpDelta(
  state: InitiativeState,
  id: string,
  delta: number,
): InitiativeState {
  const entry = state.entries.find((e) => e.id === id);
  if (!entry) return state;
  const current = (entry.hp_scratch?.current ?? 0) + delta;
  const hp_scratch = { ...entry.hp_scratch, current };
  return updateEntry(state, id, { hp_scratch });
}

/**
 * Mint a stable entry id. Purely functional — callers pass a generator.
 * Default implementation is a simple monotonic string (sufficient for
 * ephemeral session state that never leaves the client).
 */
let _nextId = 1;
export function mintEntryId(): string {
  return `initiative_${_nextId++}`;
}

/**
 * Reset the id sequence — only for tests; never call in production code.
 * @internal
 */
export function _resetEntryIdSequence(): void {
  _nextId = 1;
}
